import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: number;
  username: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
  active: boolean;
  companyId?: string;
  companyName?: string;
  emailVerified?: boolean;
  firstTimeLogin?: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  tokenType: string;
  expiresIn: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Legacy token getter for backward compatibility
  token: string | null;
  
  // Actions
  setUser: (user: User) => void;
  setTokens: (tokens: AuthTokens) => void;
  login: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateAccessToken: (accessToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      tokenType: 'Bearer',
      expiresIn: null,
      isAuthenticated: false,
      isLoading: true,
      
      // Legacy token getter (returns accessToken)
      get token() {
        const state = get();
        return state?.accessToken || null;
      },

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setTokens: (tokens: AuthTokens) => set({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        tokenType: tokens.tokenType,
        expiresIn: tokens.expiresIn,
      }),
      
      login: (user: User, tokens: AuthTokens) =>
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          tokenType: tokens.tokenType,
          expiresIn: tokens.expiresIn,
          isAuthenticated: true,
          isLoading: false,
        }),
      
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          tokenType: 'Bearer',
          expiresIn: null,
          isAuthenticated: false,
          isLoading: false,
        }),
      
      setLoading: (isLoading) => set({ isLoading }),
      
      updateAccessToken: (accessToken: string) => set({ accessToken }),
    }),
    {
      name: 'zsaas-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tokenType: state.tokenType,
        expiresIn: state.expiresIn,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Error rehydrating auth store:', error);
          // Clear corrupted state
          try {
            localStorage.removeItem('zsaas-auth');
          } catch (e) {
            console.error('Failed to clear corrupted auth state:', e);
          }
        }

        // Always set loading to false after rehydration (success or failure)
        if (state) {
          // Ensure state has all required fields with defaults
          if (state.accessToken === undefined) {
            state.accessToken = null;
          }
          if (state.refreshToken === undefined) {
            state.refreshToken = null;
          }
          if (!state.tokenType) {
            state.tokenType = 'Bearer';
          }

          // Derive auth from accessToken (activation auto-login sets token first)
          state.isAuthenticated = !!state.accessToken;

          state.setLoading(false);
        } else {
          // If state is null, initialize with defaults
          const defaultState = useAuthStore.getState();
          defaultState.setLoading(false);
        }
      },
    }
  )
);
